/**
 * Standalone store for the AtomAssist Projects layout (v2).
 *
 * v2 is one workspace, not two modes: a persistent collapsible folder
 * tree (folders + files inline) on the left and a single main stage that
 * switches between a splash, the file viewer, and the chat. The old
 * browse/chat `layoutMode` split, the chat-companion column, and the
 * separate chat-mode rail are gone.
 *
 * Only the layout chrome (tree open/width) and the expanded-folder set
 * are persisted. Stage state, the open file, and all chat state are
 * ephemeral: a reload lands on the splash with no chat carried over
 * (6.6.7). Past chats stay reachable through the per-folder history
 * dropdown, not auto-resume.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ProjectsPanelState {
  /** Persistent left tree visibility + width. */
  treeOpen: boolean;
  treeWidth: number;
}

/**
 * The single main stage's current view.
 * - "browse": at rest — the selected folder's browsable contents
 * - "viewer": a file is open in the full-stage viewer
 * - "chat": the chat fills the stage
 * Search is a local override inside the stage, not a stored view.
 *
 * A const object rather than a TS `enum` so the type stays assignable from
 * the plain string literals — `setState({ stageView: "browse" })` has to keep
 * type-checking for callers and tests. (Stage state is not persisted; only
 * treeOpen, treeWidth and expandedFolderIds are, per partialize below.)
 */
export const ProjectsStageView = {
  Browse: "browse",
  Viewer: "viewer",
  Chat: "chat",
} as const;

export type ProjectsStageView =
  (typeof ProjectsStageView)[keyof typeof ProjectsStageView];

export interface OpenFile {
  folderId: number;
  fileName: string;
  /**
   * Optional source-provenance annotations to overlay when the file opens
   * (spec #20423 Story 3). Shape mirrors DocumentViewerCore's `annotations`
   * prop (PDF) / ProjectTabularViewer's `TabularAnnotation` (CSV). Optional so
   * existing open-file callers are unaffected.
   */
  annotations?: OpenFileAnnotation[];
  /**
   * The clicked/deep-linked passage (#2281): which chunk (and page fallback)
   * the viewer should open scrolled to. Without it the viewer jumps to the
   * first cited annotation, losing which citation the user actually followed.
   */
  focus?: { chunkId?: number | null; page?: number | null };
}

/** Provenance annotation carried into the file viewer (PDF or tabular). */
export interface OpenFileAnnotation {
  chunk_id: number | string;
  /** 1-indexed PDF page (omit for tabular sources). */
  page?: number;
  bounding_boxes?: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }[];
  /** 0-indexed CSV body row (omit for PDF sources). */
  row?: number;
  cited: boolean;
  citationNumber?: number;
}

interface ProjectsLayoutStore extends ProjectsPanelState {
  /**
   * Ephemeral: the left tree rendered as a slide-over drawer below the
   * `md` breakpoint. Transient navigation chrome, never persisted.
   */
  treeDrawerOpen: boolean;
  /** Which view the single main stage is showing. Ephemeral. */
  stageView: ProjectsStageView;
  /** The file open in the viewer stage. Ephemeral. */
  openFile: OpenFile | null;
  /**
   * Where the viewer's back button returns: the stage the file was opened
   * from. "chat" only when opened via a chat citation; browse-opened files
   * return to browse so a stale chat never hijacks the back button. The
   * button returns to a chat by navigating to its URL. Ephemeral.
   */
  viewerReturnTo: "browse" | "chat";
  /**
   * Ephemeral: the project file a clicked citation should highlight in
   * the tree (6.6.6). `token` increments per click so re-citing the same
   * file re-triggers the scroll/highlight effect. Never persisted.
   */
  citationFileFocus: {
    folderId: number;
    fileName: string;
    token: number;
  } | null;
  /**
   * Folder ids whose subtree is currently expanded in the left tree.
   * Persisted (with a Set<->Array serializer) so a refresh keeps the
   * user's drill-down path.
   */
  expandedFolderIds: Set<number>;
  /** Active dialogue id per folder. Ephemeral (6.6.7) — never persisted. */
  activeChatByFolder: Record<number, number>;
  /**
   * The folder whose chat the stage is currently rendering. Set when a
   * chat is started or selected from history; fixed at creation so the
   * chat stays bound to its origin project while the tree browses
   * elsewhere. Ephemeral.
   */
  chattingFolderId: number | null;
  /** Name of the chatting folder, for the stage chip + composer placeholder. */
  chattingFolderName: string | null;

  // Layout chrome
  setProjectsPanel: (updates: Partial<ProjectsPanelState>) => void;
  setTreeOpen: (open: boolean) => void;
  toggleTree: () => void;
  setTreeWidth: (width: number) => void;
  setTreeDrawerOpen: (open: boolean) => void;

  // Stage
  setStageView: (view: ProjectsStageView) => void;
  /**
   * Open a file in the viewer stage (sets openFile + stageView). `returnTo`
   * is where the back button lands; defaults to "browse" so only an explicit
   * chat-citation open returns to chat. `annotations` overlays source-provenance
   * highlights when opened from the Sources panel (#20423 Story 3); omit for
   * plain browse/citation opens.
   */
  openFileInViewer: (
    folderId: number,
    fileName: string,
    returnTo?: "browse" | "chat",
    annotations?: OpenFileAnnotation[],
    focus?: OpenFile["focus"],
  ) => void;
  /**
   * Leave the viewer for the folder's file list. Never enters chat: only
   * `enterChatStage`, driven by a chat URL, does that.
   */
  closeViewer: () => void;
  /**
   * Selecting a folder leaves the file viewer for that folder's splash. A
   * live chat is left untouched (it stays anchored and reachable from its
   * marked node), so browsing the tree mid-chat never ejects the chat.
   */
  selectFolderStage: () => void;

  // Citations
  setCitationFileFocus: (folderId: number, fileName: string) => void;
  clearCitationFileFocus: () => void;

  // Chat
  setActiveChatForFolder: (
    folderId: number,
    chatId: number | null,
    folderName?: string,
  ) => void;
  clearActiveChatForFolder: (folderId: number) => void;
  /** Reflect the chat URL into the store (6.7.6): anchor + active dialogue. */
  enterChatStage: (
    folderId: number,
    folderName: string | null,
    dialogueId: number,
  ) => void;

  // Tree expansion
  expandFolder: (folderId: number) => void;
  collapseFolder: (folderId: number) => void;
  toggleFolderExpanded: (folderId: number) => void;
  /** Bulk-union into expandedFolderIds (auto-expand ancestors on deep-link). */
  expandFolderAncestors: (folderIds: number[]) => void;
}

const INITIAL: ProjectsPanelState = {
  treeOpen: true,
  treeWidth: 260,
};

export const useProjectsLayoutStore = create<ProjectsLayoutStore>()(
  persist(
    (set, get) => ({
      ...INITIAL,
      treeDrawerOpen: false,
      stageView: ProjectsStageView.Browse,
      openFile: null,
      viewerReturnTo: "browse",
      citationFileFocus: null,
      expandedFolderIds: new Set<number>(),
      activeChatByFolder: {},
      chattingFolderId: null,
      chattingFolderName: null,

      setProjectsPanel: (updates) => set((state) => ({ ...state, ...updates })),
      setTreeOpen: (treeOpen) =>
        set((state) => (state.treeOpen === treeOpen ? state : { treeOpen })),
      toggleTree: () => set((state) => ({ treeOpen: !state.treeOpen })),
      setTreeWidth: (treeWidth) => {
        if (treeWidth === get().treeWidth) return;
        set({ treeWidth });
      },
      setTreeDrawerOpen: (treeDrawerOpen) => set({ treeDrawerOpen }),

      setStageView: (stageView) =>
        set((state) => (state.stageView === stageView ? state : { stageView })),
      openFileInViewer: (
        folderId,
        fileName,
        returnTo = "browse",
        annotations,
        focus,
      ) =>
        set({
          openFile: { folderId, fileName, annotations, focus },
          stageView: ProjectsStageView.Viewer,
          viewerReturnTo: returnTo,
        }),
      closeViewer: () =>
        set({ openFile: null, stageView: ProjectsStageView.Browse }),
      selectFolderStage: () =>
        set((state) =>
          state.stageView === ProjectsStageView.Viewer
            ? { stageView: ProjectsStageView.Browse, openFile: null }
            : state,
        ),

      setCitationFileFocus: (folderId, fileName) =>
        set((state) => ({
          citationFileFocus: {
            folderId,
            fileName,
            token: (state.citationFileFocus?.token ?? 0) + 1,
          },
        })),
      clearCitationFileFocus: () =>
        set((state) =>
          state.citationFileFocus === null
            ? state
            : { citationFileFocus: null },
        ),

      setActiveChatForFolder: (folderId, chatId, folderName) =>
        set((state) => {
          const next = { ...state.activeChatByFolder };
          if (chatId === null) {
            delete next[folderId];
          } else {
            next[folderId] = chatId;
          }
          const isCleared = chatId === null;
          return {
            activeChatByFolder: next,
            chattingFolderId: isCleared ? null : folderId,
            chattingFolderName: isCleared
              ? null
              : (folderName ?? state.chattingFolderName),
          };
        }),
      // 6.7.6: the chat URL (/folder/:id/chat/:dialogueId) is the source of
      // truth for the chat stage. FolderBrowser calls this to reflect the URL
      // into the store on open / deep-link / refresh. Idempotent so the
      // sync effect doesn't churn renders.
      enterChatStage: (folderId, folderName, dialogueId) =>
        set((state) => {
          const sameChat = state.activeChatByFolder[folderId] === dialogueId;
          const nextName = folderName ?? state.chattingFolderName;
          if (
            state.stageView === ProjectsStageView.Chat &&
            state.chattingFolderId === folderId &&
            sameChat &&
            state.chattingFolderName === nextName
          ) {
            return state;
          }
          return {
            stageView: ProjectsStageView.Chat,
            chattingFolderId: folderId,
            chattingFolderName: nextName,
            activeChatByFolder: sameChat
              ? state.activeChatByFolder
              : { ...state.activeChatByFolder, [folderId]: dialogueId },
          };
        }),
      clearActiveChatForFolder: (folderId) =>
        set((state) => {
          if (!(folderId in state.activeChatByFolder)) return state;
          const next = { ...state.activeChatByFolder };
          delete next[folderId];
          const clearCurrent = state.chattingFolderId === folderId;
          return {
            activeChatByFolder: next,
            chattingFolderId: clearCurrent ? null : state.chattingFolderId,
            chattingFolderName: clearCurrent ? null : state.chattingFolderName,
          };
        }),

      expandFolder: (folderId) =>
        set((state) => {
          if (state.expandedFolderIds.has(folderId)) return state;
          const next = new Set(state.expandedFolderIds);
          next.add(folderId);
          return { expandedFolderIds: next };
        }),
      collapseFolder: (folderId) =>
        set((state) => {
          if (!state.expandedFolderIds.has(folderId)) return state;
          const next = new Set(state.expandedFolderIds);
          next.delete(folderId);
          return { expandedFolderIds: next };
        }),
      toggleFolderExpanded: (folderId) =>
        set((state) => {
          const next = new Set(state.expandedFolderIds);
          if (next.has(folderId)) {
            next.delete(folderId);
          } else {
            next.add(folderId);
          }
          return { expandedFolderIds: next };
        }),
      expandFolderAncestors: (folderIds) =>
        set((state) => {
          // Skip the work (and the re-render) when every id is already in.
          let allPresent = true;
          for (const id of folderIds) {
            if (!state.expandedFolderIds.has(id)) {
              allPresent = false;
              break;
            }
          }
          if (allPresent) return state;
          const next = new Set(state.expandedFolderIds);
          for (const id of folderIds) next.add(id);
          return { expandedFolderIds: next };
        }),
    }),
    {
      name: "projects-layout-store",
      // expandedFolderIds is a Set, which JSON.stringify renders as `{}`.
      // Round-trip it through Array so a refresh restores the actual
      // expanded set. Stage/open-file/chat state are intentionally absent:
      // a reload rehydrates on the splash with no chat (6.6.7).
      partialize: (state) => ({
        treeOpen: state.treeOpen,
        treeWidth: state.treeWidth,
        expandedFolderIds: Array.from(state.expandedFolderIds),
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          Omit<ProjectsLayoutStore, "expandedFolderIds">
        > & { expandedFolderIds?: number[] };
        const expandedFolderIds = Array.isArray(persisted.expandedFolderIds)
          ? new Set<number>(persisted.expandedFolderIds)
          : currentState.expandedFolderIds;
        return {
          ...currentState,
          ...persisted,
          expandedFolderIds,
        };
      },
    },
  ),
);
